import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';

//#region CONST LoginProgress
/**
 * Represents the predefined steps involved in the user login process.
 *
 * Each step has a specific purpose, such as verifying login credentials,
 * retrieving user details, fetching access rights, and obtaining user-specific settings.
 *
 * @constant
 * @type {ILoginSteps}
 * @description This constant contains the predefined steps for the user login process. It tracks the progress of each step, such as setting credentials, verifying login, and fetching user data.
 * @example
 * const loginSteps: ILoginSteps = {
 *   progress: {
 *     currentStep: 1,
 *     totalSteps: 5,
 *     percentComplete: 20
 *   },
 *   1: {
 *     sequence: 1,
 *     description: 'Verify user login credentials',
 *     completed: false
 *   }
 * };
 */
export const LoginProgress: ILoginSteps = {
  progress: {
    currentStep: 0,
    totalSteps: 5,
    percentComplete: 0,
  },
  ['0']: {
    sequence: 0,
    description: 'Setting user login credentials',
    completed: true,
  },
  ['1']: {
    sequence: 1,
    description: 'Verify user login credentials',
    completed: false,
  },
  ['2']: {
    sequence: 2,
    description: 'Retrieve complete user information',
    completed: false,
  },
  ['3']: {
    sequence: 3,
    description: 'Fetch initial user access rights',
    completed: false,
  },
  ['4']: {
    sequence: 4,
    description: 'Load feature user stores with user access.',
    completed: false,
  },
  ['5']: {
    sequence: 5,
    description: 'Load user notifications',
    completed: false,
  },
  //SettingsActions.getAllUserSettings(),
  //SettingsActions.getAllUserStoreSettings())
  // ['6']: {
  //   sequence: 6,
  //   description: 'Load application-specific user settings', //
  //   completed: false,
  // },
};

//#endregion

//#region INTERFACE ILoginStep
/**
 * Represents a single step in the user login flow.
 *
 * This is used to track the current progress of each step in the login process,
 * such as login, retrieving user details, access rights, and settings.
 *
 * @interface ILoginStep
 * @description This interface defines a single step in the login flow, including its sequence, description, and completion status.
 */
export interface ILoginStep {
  /**
   * Indicates whether this login step has been completed.
   *
   * @type {boolean}
   */
  completed: boolean;

  /**
   * A human-readable description of the login step.
   *
   * Example: "Get User Access"
   *
   * @type {string}
   */
  description: string;

  /**
   * The step number in the login sequence.
   *
   * Example: 1 for "Login", 2 for "Get User Details"
   *
   * @type {number}
   */
  sequence: number;
}

//#endregion

//#region INTERFACE ILoginSteps
/**
 * ### ILoginSteps
 * A collection of login steps indexed by their sequence number.
 *
 * This interface represents the sequence of steps in the login process,
 * along with progress tracking and any errors encountered during the login flow.
 *
 * @interface ILoginSteps
 * @description This interface maps each login step to its respective details, including:
 * - The current progress of the login flow, tracked by the `progress` property.
 * - An optional error property, `error`, that holds any global error information encountered during the process.
 * - A dynamic mapping of login steps, where each key corresponds to the sequence number of the step.
 *
 * @example
 * const loginSteps: ILoginSteps = {
 *   progress: { currentStep: 2, totalSteps: 5, percentage: 40 },
 *   1: { sequence: 1, description: "Verify User Login", completed: false },
 *   2: { sequence: 2, description: "Verify Password", completed: true }
 *   error: { message: "Invalid credentials" },
 * };
 */
export interface ILoginSteps extends Object {
  /**
   * The current progress of the login steps, including the total number of steps,
   * the current step, and the percentage of completion.
   *
   * @type {ILoginStepProgress}
   * @description This property tracks how far along the user is in the login process.
   */
  progress: ILoginStepProgress;

  /**
   * A dynamic mapping of the login steps, indexed by their sequence number.
   * Each key corresponds to a step's sequence number and contains details about the step's status.
   *
   * Example:
   * ```ts
   * {
   *   1: { sequence: 1, description: "Verify User Login", completed: false }
   * }
   * ```
   *
   * @type {ILoginStep}
   * @description Each step contains information about the sequence, description, and completion
   * status, allowing the application to track and display the user's progress through the login flow.
   */
  [sequence: number]: ILoginStep;

  /**
   * An optional global error object that holds error information if any issue occurs
   * during the login process.
   *
   * @type {IGlobalError}
   * @optional
   * @description The `error` property captures global error metadata, such as error messages
   * and codes, to be displayed to the user during the login process.
   */
  error?: IGlobalError;
}

//#endregion

//#region INTERFACE ILoginStepProgress
/**
 * Represents the progress of the login steps.
 *
 * This interface tracks the total number of steps, the current step, and
 * the percentage of completion of the login flow.
 *
 * @interface ILoginStepProgress
 * @description This interface defines the overall progress of the login process, including the current step, total steps, and the percentage of completion.
 */
export interface ILoginStepProgress {
  /**
   * The current step number in the login flow.
   *
   * Example: 2 (indicating step 2 is currently in progress)
   *
   * @type {number}
   */
  currentStep: number;

  /**
   * The percentage of completion of the login flow, calculated as
   * `(currentStep / totalSteps) * 100`.
   *
   * Example: 50 (indicating the login flow is halfway completed)
   *
   * @type {number}
   */
  percentComplete: number;

  /**
   * The total number of steps in the login process.
   *
   * Example: 4
   *
   * @type {number}
   */
  totalSteps: number;
}

//#endregion

